import { utcToZonedTime } from 'date-fns-tz'
import { format } from 'date-fns'
import type { PermanenceInfo } from '@/types/permanence.types'

export interface PermanenceLinkRowData {
  direction: 'forward' | 'backward'
  stationLabel: string
  doctorStationId: number
  stationId: number
  recordId: number
}

/**
 * Shape mínimo (duck-typed) que os utilitários desta feature precisam de um
 * check — compatível estruturalmente com `HistoryDate`, `HistoryDateRequest`
 * e `DoctorCheck`, sem forçar import cruzado de nenhum desses tipos aqui.
 */
export interface PermanenceCheckLike {
  checkin?: string | Date | null
  checkout?: string | Date | null
  permanence?: PermanenceInfo | null
}

/**
 * Extrai as linhas de "vínculo de Registro de Permanência" a partir dos checks
 * de UM plantão. Um plantão "do meio" de uma cadeia A→B→C tem os dois vínculos
 * (veio de A + continua para C) no mesmo check principal — por isso um único
 * check pode gerar até 2 linhas.
 */
export function getPermanenceLinkRows(checks: PermanenceCheckLike[]): PermanenceLinkRowData[] {
  const rows: PermanenceLinkRowData[] = []

  for (const check of checks) {
    if (check.permanence?.continuesTo) {
      rows.push({
        direction: 'forward',
        stationLabel: check.permanence.continuesTo.stationLabel,
        doctorStationId: check.permanence.continuesTo.doctorStationId,
        stationId: check.permanence.continuesTo.stationId,
        recordId: check.permanence.continuesTo.recordId
      })
    }
    if (check.permanence?.cameFrom) {
      rows.push({
        direction: 'backward',
        stationLabel: check.permanence.cameFrom.stationLabel,
        doctorStationId: check.permanence.cameFrom.doctorStationId,
        stationId: check.permanence.cameFrom.stationId,
        recordId: check.permanence.cameFrom.recordId
      })
    }
  }

  return rows
}

function formatHHmm(value: string | Date | null | undefined, timezone: string): string {
  if (!value) return '--:--'
  const date = typeof value === 'string' ? new Date(value) : value
  if (isNaN(date.getTime())) return '--:--'
  try {
    return format(utcToZonedTime(date, timezone), 'HH:mm')
  } catch {
    // Fuso horário inválido/não reconhecido — mesmo valor de fallback já
    // usado acima para data inválida, para o render da grade nunca lançar.
    return '--:--'
  }
}

/**
 * Monta o texto do tooltip do ícone de Permanência na grade (fileira de ícones
 * de status da célula, `withIcon.vue`). `null` quando nenhum check do plantão
 * tem permanência automática — a grade não deve exibir o ícone nesse caso.
 */
export function getGradePermanenceTooltip(
  checks: PermanenceCheckLike[],
  timezone: string
): string | null {
  for (const check of checks) {
    if (check.permanence?.autoCheckin && check.permanence.cameFrom) {
      const time = formatHHmm(check.checkin, timezone)
      return `Registro de Permanência: check-in automático às ${time}, continuidade do plantão ${check.permanence.cameFrom.stationLabel}`
    }
    if (check.permanence?.autoCheckout && check.permanence.continuesTo) {
      const time = formatHHmm(check.checkout, timezone)
      return `Registro de Permanência: check-out automático às ${time}, continuidade do plantão ${check.permanence.continuesTo.stationLabel}`
    }
  }
  return null
}

/**
 * Decide se `stationDetailsModal.vue` deve refazer o fetch quando o
 * `stationId` recebido via prop `infos` muda ENQUANTO o modal já está aberto
 * — ex.: o usuário clicou na linha de vínculo de Registro de Permanência e o
 * consumidor (Dashboard/Index.vue etc.) atualizou `stationSelected.stationId`
 * no lugar, sem fechar/reabrir o modal. O fetch inicial (modal abrindo agora)
 * continua coberto pelo watch existente em `props.showModal` — esta função
 * cobre só o caso de troca "por baixo" com o modal já visível.
 */
export function shouldRefetchOnStationIdChange(
  isModalOpen: boolean,
  newStationId: number | undefined,
  oldStationId: number | undefined
): boolean {
  return Boolean(isModalOpen && newStationId && newStationId !== oldStationId)
}

/**
 * Aplica a navegação para o plantão vinculado no estado `stationSelected` já
 * existente nos consumidores de `StationDetailsModal` (Dashboard/Index.vue,
 * AuditoriumExceptionalities/index.vue, Approvals/AuditoriumExceptionalities/index.vue):
 * troca só o `stationId`, preserva o resto do objeto (companyId, healthUnit
 * etc. continuam válidos o suficiente para o modal buscar os novos detalhes).
 * `null` quando ainda não há seleção — não há o que navegar.
 */
export function withNavigatedStationId<T extends { stationId: number }>(
  current: T | null | undefined,
  stationId: number
): T | null {
  if (!current) return null
  return { ...current, stationId }
}

export interface PermanenceVisibility {
  showCheckinChip: boolean
  showCheckoutChip: boolean
  links: PermanenceLinkRowData[]
}

/**
 * Decide o que `stationDetailsModal.vue` deve exibir da feature de Registro de
 * Permanência — chips de check-in/check-out e linhas de vínculo — já
 * considerando a feature flag. Gate no site de consumo (mesmo padrão adotado
 * por `doctor.checks.vue`, commit `f6c34261`): nem `PermanenceLinkRow.vue`
 * (peça de apresentação pura) nem `getPermanenceLinkRows` sabem da flag.
 *
 * Extraída como função pura (em vez de ficar só como `&&` inline no template)
 * porque `stationDetailsModal.vue` não tem nenhum teste de mount (ver "Notas
 * de divergência" #6 do plano) — sem isso, o cenário de flag desligada
 * ficaria sem nenhuma cobertura automatizada.
 */
export function getPermanenceVisibility(
  flagEnabled: boolean,
  mainCheck: PermanenceCheckLike | null | undefined
): PermanenceVisibility {
  if (!flagEnabled || !mainCheck) {
    return { showCheckinChip: false, showCheckoutChip: false, links: [] }
  }

  return {
    showCheckinChip: Boolean(mainCheck.permanence?.autoCheckin),
    showCheckoutChip: Boolean(mainCheck.permanence?.autoCheckout),
    links: getPermanenceLinkRows([mainCheck])
  }
}
